import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useLocalizedPath } from '@/lib/i18n/useLocalizedPath';
import { isNavLinkActive } from '@/lib/navActiveHelper';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Shield, Truck, CreditCard } from 'lucide-react';
import logo from '@/assets/fake-id-logo-brand.svg';
import { VisaIcon, MastercardIcon, ApplePayIcon, GooglePayIcon, SepaIcon, BankTransferIcon } from './icons/PaymentIcons';
import { LocalizedLink } from './LocalizedLink';

const footerLinks = {
  shop: [
    { key: 'nav.home', href: '/' },
    { key: 'nav.shop', href: '/shop' },
    { key: 'nav.reviews', href: '/reviews' },
    { key: 'nav.blog', href: '/blog' },
  ],
  company: [
    { key: 'nav.about', href: '/about' },
    { key: 'nav.howTo', href: '/instructions-how-to-make-a-fake-id' },
    { key: 'nav.contact', href: '/contact' },
  ],
  legal: [
    { key: 'footer.imprint', href: '/imprint' },
    { key: 'footer.privacy', href: '/privacy' },
    { key: 'footer.terms', href: '/terms' },
    { key: 'footer.refund', href: '/refund' },
    { key: 'footer.withdrawal', href: '/withdrawal' },
    { key: 'footer.cookies', href: '/cookies' },
  ],
};


const trustBadgesI18n: Record<string, { secure: string; shipping: string; payment: string }> = {
  en: { secure: 'Secure Checkout', shipping: 'Fast Shipping', payment: 'Easy Payment' },
  de: { secure: 'Sicherer Checkout', shipping: 'Schneller Versand', payment: 'Einfache Bezahlung' },
  es: { secure: 'Pago Seguro', shipping: 'Envío Rápido', payment: 'Pago Fácil' },
  fr: { secure: 'Paiement Sécurisé', shipping: 'Livraison Rapide', payment: 'Paiement Facile' },
  pt: { secure: 'Pagamento Seguro', shipping: 'Envio Rápido', payment: 'Pagamento Fácil' },
  ru: { secure: 'Безопасная оплата', shipping: 'Быстрая доставка', payment: 'Удобная оплата' },
  it: { secure: 'Pagamento Sicuro', shipping: 'Spedizione Veloce', payment: 'Pagamento Facile' },
  sv: { secure: 'Säker Betalning', shipping: 'Snabb Frakt', payment: 'Enkel Betalning' },
  zh: { secure: '安全结账', shipping: '快速配送', payment: '便捷支付' },
};

const brandDescriptionI18n: Record<string, string> = {
  en: 'Premium novelty ID cards with stunning designs. Fast worldwide delivery and exceptional quality.',
  de: 'Premium-Phantasieausweise mit beeindruckenden Designs. Schneller weltweiter Versand und außergewöhnliche Qualität.',
  es: 'Tarjetas de identificación de novedad premium con diseños impresionantes. Envío mundial rápido y calidad excepcional.',
  fr: 'Cartes d\'identité de nouveauté premium aux designs saisissants. Livraison mondiale rapide et qualité exceptionnelle.',
  pt: 'Cartões de identificação de novidade premium com designs impressionantes. Entrega mundial rápida e qualidade excepcional.',
  ru: 'Премиальные новинки удостоверений с потрясающим дизайном. Быстрая доставка по всему миру и исключительное качество.',
  it: 'Carte d\'identità novità premium con design straordinari. Spedizione mondiale rapida e qualità eccezionale.',
  sv: 'Premium novelty ID-kort med fantastiska designer. Snabb världsomspännande leverans och exceptionell kvalitet.',
  zh: '精美设计的优质新奇ID卡。快速全球配送，品质卓越。',
};

const securePaymentsI18n: Record<string, string> = {
  en: 'Secure payments:',
  de: 'Sichere Zahlungsmethoden:',
  es: 'Pagos seguros:',
  fr: 'Paiements sécurisés :',
  pt: 'Pagamentos seguros:',
  ru: 'Безопасные платежи:',
  it: 'Pagamenti sicuri:',
  sv: 'Säkra betalningar:',
  zh: '安全支付：',
};

// SEO-optimized footer text with keywords naturally integrated – extended version
const seoFooterText = {
  en: `Fake-ID.com is the world's most trusted source for premium novelty identification cards, serving over 63,000 satisfied customers across 40+ countries since 2007. Our fake ID cards are meticulously crafted with authentic-looking holograms, UV-reactive security features, and scannable barcodes — making them perfect for entertainment, film and TV productions, theatrical props, cosplay events, bachelor parties, birthday gifts, and serious collectors.

Whether you're looking for a realistic fake Drivers License, a scannable State ID, a custom Photo ID, a professional Press ID, a fun Student ID, or an international-style National ID — our ID card generator lets you fully personalize every detail online. Choose from dozens of signature fonts, upload your own photo, and preview your card in real-time before ordering. Every card is printed on premium PVC with multi-layer holographic overlays and precision color matching for an unmatched level of realism.

We ship worldwide with fast, discrete packaging — most orders arrive within 5–10 business days. Our secure checkout supports Visa, Mastercard, Apple Pay, Google Pay, SEPA, and bank transfer. Every order is backed by our satisfaction guarantee and responsive customer support team. Read thousands of verified fake ID reviews from real customers to see why we're rated #1 for quality, speed, and trust. All products are novelty items intended for legal entertainment purposes only — we do not produce real government-issued documents.`,

  de: `Fake-ID.com ist der weltweit führende Anbieter für Premium-Phantasieausweise und Spaßausweise — seit 2007 vertrauen uns über 63.000 zufriedene Kunden in mehr als 40 Ländern. Unsere Fake-Ausweise werden mit höchster Präzision gefertigt: authentisch wirkende Hologramme, UV-reaktive Sicherheitsmerkmale und scannbare Barcodes machen sie ideal für Unterhaltung, Film- und TV-Produktionen, Theater-Requisiten, Cosplay, JGA-Partys, Geburtstagsgeschenke und anspruchsvolle Sammler.

Ob Sie einen realistischen Fake-Führerschein, einen scannbaren Personalausweis, einen personalisierten Fotoausweis, einen professionellen Presseausweis, einen lustigen Studentenausweis oder einen internationalen Ausweis im Nationalausweis-Design suchen — unser Ausweis-Generator ermöglicht die vollständige Online-Personalisierung. Wählen Sie aus Dutzenden Unterschrift-Schriftarten, laden Sie Ihr eigenes Foto hoch und sehen Sie eine Echtzeit-Vorschau Ihrer Karte vor der Bestellung. Jede Karte wird auf Premium-PVC gedruckt, mit mehrschichtigen holographischen Overlays und präziser Farbabstimmung für ein unübertroffenes Maß an Realismus.

Wir liefern weltweit mit schnellem, diskretem Versand — die meisten Bestellungen treffen innerhalb von 5–10 Werktagen ein. Unser sicherer Checkout unterstützt Visa, Mastercard, Apple Pay, Google Pay, SEPA und Banküberweisung. Jede Bestellung wird durch unsere Zufriedenheitsgarantie und unser reaktionsschnelles Support-Team abgesichert. Lesen Sie tausende verifizierte Fake-ID-Bewertungen von echten Kunden und erfahren Sie, warum wir die Nr. 1 für Qualität, Geschwindigkeit und Vertrauen sind. Alle Produkte sind Phantasie- und Spaßartikel, ausschließlich für legale Unterhaltungszwecke bestimmt — wir stellen keine echten amtlichen Dokumente her.`,

  es: `Fake-ID.com es la fuente más confiable del mundo para tarjetas de identificación de novedad premium, con más de 63.000 clientes satisfechos en más de 40 países desde 2007. Nuestras identificaciones falsas están meticulosamente elaboradas con hologramas de aspecto auténtico, características de seguridad reactivas a UV y códigos de barras escaneables — ideales para entretenimiento, producciones de cine y televisión, atrezzo teatral, eventos de cosplay, despedidas de soltero, regalos de cumpleaños y coleccionistas serios.

Ya sea que busque una licencia de conducir falsa realista, una identificación estatal escaneable, una identificación con foto personalizada, una identificación de prensa profesional, una identificación de estudiante divertida o una identificación nacional de estilo internacional — nuestro generador de tarjetas de identificación le permite personalizar completamente cada detalle en línea. Elija entre docenas de fuentes de firma, suba su propia foto y previsualice su tarjeta en tiempo real antes de ordenar. Cada tarjeta se imprime en PVC premium con superposiciones holográficas multicapa y coincidencia de color de precisión.

Enviamos a todo el mundo con embalaje rápido y discreto — la mayoría de los pedidos llegan en 5–10 días hábiles. Nuestro pago seguro acepta Visa, Mastercard, Apple Pay, Google Pay, SEPA y transferencia bancaria. Todos los productos son artículos de novedad destinados exclusivamente a fines de entretenimiento legal — no producimos documentos gubernamentales reales.`,

  fr: `Fake-ID.com est la source la plus fiable au monde pour les cartes d'identité de nouveauté premium, servant plus de 63 000 clients satisfaits dans plus de 40 pays depuis 2007. Nos fausses cartes d'identité sont méticuleusement conçues avec des hologrammes d'aspect authentique, des caractéristiques de sécurité réactives aux UV et des codes-barres scannables — parfaites pour le divertissement, les productions cinématographiques et télévisuelles, les accessoires de théâtre, les événements cosplay, les enterrements de vie de garçon, les cadeaux d'anniversaire et les collectionneurs sérieux.

Que vous recherchiez un faux permis de conduire réaliste, une carte d'identité d'État scannable, une carte d'identité photo personnalisée, une carte de presse professionnelle, une carte d'étudiant amusante ou une carte d'identité nationale de style international — notre générateur de cartes d'identité vous permet de personnaliser entièrement chaque détail en ligne. Choisissez parmi des dizaines de polices de signature, téléchargez votre propre photo et prévisualisez votre carte en temps réel avant de commander. Chaque carte est imprimée sur PVC premium avec des superpositions holographiques multicouches.

Nous livrons dans le monde entier avec un emballage rapide et discret — la plupart des commandes arrivent sous 5 à 10 jours ouvrables. Notre paiement sécurisé accepte Visa, Mastercard, Apple Pay, Google Pay, SEPA et virement bancaire. Tous les produits sont des articles de nouveauté destinés exclusivement à des fins de divertissement légal — nous ne produisons pas de vrais documents gouvernementaux.`,

  pt: `Fake-ID.com é a fonte mais confiável do mundo para cartões de identificação de novidade premium, atendendo mais de 63.000 clientes satisfeitos em mais de 40 países desde 2007. Nossos cartões de identificação falsos são meticulosamente elaborados com hologramas de aparência autêntica, recursos de segurança reativos a UV e códigos de barras escaneáveis — perfeitos para entretenimento, produções de cinema e TV, adereços teatrais, eventos de cosplay, despedidas de solteiro, presentes de aniversário e colecionadores sérios.

Se você está procurando uma carteira de motorista falsa realista, um ID estadual escaneável, um ID com foto personalizado, um ID de imprensa profissional, um ID de estudante divertido ou um ID nacional de estilo internacional — nosso gerador de cartões de identificação permite personalizar completamente cada detalhe online. Escolha entre dezenas de fontes de assinatura, faça upload de sua própria foto e visualize seu cartão em tempo real antes de fazer o pedido.

Enviamos para todo o mundo com embalagem rápida e discreta — a maioria dos pedidos chega em 5 a 10 dias úteis. Nosso checkout seguro aceita Visa, Mastercard, Apple Pay, Google Pay, SEPA e transferência bancária. Todos os produtos são itens de novidade destinados exclusivamente a fins de entretenimento legal — não produzimos documentos governamentais reais.`,

  ru: `Fake-ID.com — самый надежный в мире источник премиальных новинок удостоверений личности, обслуживающий более 63 000 довольных клиентов в более чем 40 странах с 2007 года. Наши поддельные удостоверения личности тщательно изготовлены с реалистичными голограммами, УФ-реактивными элементами безопасности и сканируемыми штрих-кодами — идеально подходят для развлечений, кино- и телепроизводства, театрального реквизита, косплей-мероприятий, мальчишников, подарков на день рождения и серьезных коллекционеров.

Будь то реалистичные поддельные водительские права, сканируемое государственное удостоверение, персонализированное фото-удостоверение, профессиональная пресс-карта, веселый студенческий билет или удостоверение в международном стиле — наш генератор удостоверений позволяет полностью персонализировать каждую деталь онлайн. Выбирайте из десятков шрифтов подписи, загружайте свою фотографию и просматривайте карту в реальном времени перед заказом.

Мы доставляем по всему миру в быстрой дискретной упаковке — большинство заказов прибывает в течение 5–10 рабочих дней. Наша безопасная оплата принимает Visa, Mastercard, Apple Pay, Google Pay, SEPA и банковский перевод. Все продукты являются новинками, предназначенными исключительно для законных развлекательных целей — мы не производим настоящие государственные документы.`,

  it: `Fake-ID.com è la fonte più affidabile al mondo per carte d'identità novità premium, con oltre 63.000 clienti soddisfatti in più di 40 paesi dal 2007. Le nostre carte d'identità false sono meticolosamente realizzate con ologrammi dall'aspetto autentico, caratteristiche di sicurezza reattive ai raggi UV e codici a barre scansionabili — perfette per intrattenimento, produzioni cinematografiche e televisive, oggetti di scena teatrali, eventi cosplay, addii al celibato, regali di compleanno e collezionisti seri.

Che tu stia cercando una patente di guida falsa realistica, un ID statale scansionabile, un ID fotografico personalizzato, una tessera stampa professionale, una tessera studente divertente o un ID nazionale in stile internazionale — il nostro generatore di carte d'identità ti permette di personalizzare completamente ogni dettaglio online. Scegli tra dozzine di font per la firma, carica la tua foto e visualizza l'anteprima della tua carta in tempo reale prima di ordinare.

Spediamo in tutto il mondo con imballaggio rapido e discreto — la maggior parte degli ordini arriva entro 5–10 giorni lavorativi. Il nostro checkout sicuro accetta Visa, Mastercard, Apple Pay, Google Pay, SEPA e bonifico bancario. Tutti i prodotti sono articoli novità destinati esclusivamente a scopi di intrattenimento legale — non produciamo documenti governativi reali.`,

  sv: `Fake-ID.com är världens mest pålitliga källa för premium novelty ID-kort, med över 63 000 nöjda kunder i 40+ länder sedan 2007. Våra falska ID-kort är noggrant tillverkade med autentiskt utseende hologram, UV-reaktiva säkerhetsfunktioner och skannbara streckkoder — perfekta för underhållning, film- och TV-produktioner, teaterrekvisita, cosplay-evenemang, svensexor, födelsedagspresenter och seriösa samlare.

Oavsett om du letar efter ett realistiskt falskt körkort, ett skannbart statligt ID, ett anpassat foto-ID, ett professionellt press-ID, ett roligt student-ID eller ett internationellt national-ID — vår ID-kortsgenerator låter dig helt anpassa varje detalj online. Välj bland dussintals signaturfonter, ladda upp ditt eget foto och förhandsgranska ditt kort i realtid innan du beställer.

Vi skickar över hela världen med snabb, diskret förpackning — de flesta beställningar anländer inom 5–10 arbetsdagar. Vår säkra kassa accepterar Visa, Mastercard, Apple Pay, Google Pay, SEPA och banköverföring. Alla produkter är novelty-artiklar avsedda uteslutande för lagliga underhållningsändamål — vi producerar inga riktiga statliga dokument.`,

  zh: `Fake-ID.com是全球最值得信赖的优质新奇身份证卡来源，自2007年以来已为40多个国家的63,000多位满意客户提供服务。我们的仿真ID卡采用精密工艺制作，配有逼真的全息图、紫外线反应安全特征和可扫描条形码——非常适合娱乐、影视制作、舞台道具、角色扮演活动、单身派对、生日礼物和认真的收藏家。

无论您是在寻找逼真的仿真驾照、可扫描的州ID、定制照片ID、专业记者证、有趣的学生证还是国际风格的国民身份证——我们的ID卡生成器让您可以在线完全个性化每一个细节。从数十种签名字体中选择，上传您自己的照片，并在下单前实时预览您的卡片。

我们向全球发货，采用快速、低调的包装——大多数订单在5-10个工作日内送达。我们的安全结账支持Visa、Mastercard、Apple Pay、Google Pay、SEPA和银行转账。所有产品均为新奇物品，仅供合法娱乐用途——我们不生产真实的政府颁发文件。`,
};

export const Footer: React.FC = () => {
  const { t, language } = useLanguage();
  const { stripLanguagePrefix } = useLocalizedPath();
  const location = useLocation();
  const currentPath = stripLanguagePrefix(location.pathname);
  
  // Get SEO text for current language, fallback to English
  const currentSeoText = seoFooterText[language as keyof typeof seoFooterText] || seoFooterText.en;

  return (
    <div className="bg-background py-6 md:py-12 px-4 md:px-0">
      <footer className="container mx-auto bg-card/50 border border-border/30 rounded-2xl overflow-hidden">
        {/* Trust Badges */}
        <div className="border-b border-border/30">
          <div className="px-4 md:px-12 py-4 md:py-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 md:gap-16">
              {([
                { icon: Shield, text: (trustBadgesI18n[language] || trustBadgesI18n.en).secure },
                { icon: Truck, text: (trustBadgesI18n[language] || trustBadgesI18n.en).shipping },
                { icon: CreditCard, text: (trustBadgesI18n[language] || trustBadgesI18n.en).payment },
              ]).map((badge, index) => (
                <div key={index} className="flex items-center gap-2 md:gap-3 text-muted-foreground">
                  <badge.icon className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                  <span className="font-medium text-sm md:text-base">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Footer */}
        <div className="px-4 md:px-12 py-8 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8 lg:gap-12">
            {/* Brand */}
            <div className="col-span-2 lg:col-span-2 text-center md:text-left mb-4 md:mb-0">
              <LocalizedLink to="/" className="inline-block mb-3 md:mb-4">
                <img src={logo} alt="fake-id" className="h-5 md:h-6" />
              </LocalizedLink>
              <p className="text-muted-foreground text-sm md:text-base max-w-sm mx-auto md:mx-0">
                {brandDescriptionI18n[language] || brandDescriptionI18n.en}
              </p>
            </div>

            {/* Shop Links */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 md:mb-4 text-sm md:text-base">{t('nav.shop')}</h3>
              <ul className="space-y-2 md:space-y-3">
                {footerLinks.shop.map((link) => (
                  <li key={link.key}>
                    <LocalizedLink
                      to={link.href}
                      className={`transition-colors text-sm md:text-base ${
                        isNavLinkActive(currentPath, link.href)
                          ? 'text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t(link.key)}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="font-semibold text-foreground mb-3 md:mb-4 text-sm md:text-base">{t('footer.company')}</h3>
              <ul className="space-y-2 md:space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.key}>
                    <LocalizedLink
                      to={link.href}
                      className={`transition-colors text-sm md:text-base ${
                        isNavLinkActive(currentPath, link.href)
                          ? 'text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t(link.key)}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal & Language */}
            <div className="col-span-2 lg:col-span-1 pt-4 md:pt-0 border-t md:border-t-0 border-border/30">
              <h3 className="font-semibold text-foreground mb-3 md:mb-4 text-sm md:text-base">{t('footer.legal')}</h3>
              <div className="flex flex-wrap md:flex-col gap-2 md:gap-0 md:space-y-3 mb-4 md:mb-6">
                {footerLinks.legal.map((link) => (
                  <LocalizedLink
                    key={link.key}
                    to={link.href}
                    className={`transition-colors text-sm md:text-base whitespace-nowrap ${
                      isNavLinkActive(currentPath, link.href)
                        ? 'text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t(link.key)}
                  </LocalizedLink>
                ))}
              </div>
              <div className="pt-3 md:pt-4 border-t border-border/30">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </div>

        {/* SEO Text Block */}
        <div className="border-t border-border/30">
          <div className="px-4 md:px-12 py-6 md:py-8">
            <p className="text-xs md:text-sm text-muted-foreground leading-relaxed max-w-5xl mx-auto text-center">
              {currentSeoText}
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border/30">
          <div className="px-4 md:px-12 py-4 md:py-6 flex flex-col gap-4 md:gap-6">
            {/* Payment Methods */}
            <div className="flex flex-col items-center gap-3">
              <span className="text-xs text-muted-foreground">{securePaymentsI18n[language] || securePaymentsI18n.en}</span>
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
                <div className="rounded overflow-hidden">
                  <VisaIcon className="h-6 md:h-8 w-auto" />
                </div>
                <div className="rounded overflow-hidden">
                  <MastercardIcon className="h-6 md:h-8 w-auto" />
                </div>
                <div className="rounded overflow-hidden">
                  <ApplePayIcon className="h-6 md:h-8 w-auto" />
                </div>
                <div className="rounded overflow-hidden">
                  <GooglePayIcon className="h-6 md:h-8 w-auto" />
                </div>
                <div className="rounded overflow-hidden">
                  
                </div>
                <div className="rounded overflow-hidden">
                  <SepaIcon className="h-6 md:h-8 w-auto" />
                </div>
                <div className="rounded overflow-hidden">
                  <BankTransferIcon className="h-6 md:h-8 w-auto" />
                </div>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
              <p className="text-xs md:text-sm text-muted-foreground text-center md:text-left">
                © 2007 - {new Date().getFullYear()} Fake-ID.com | {t('footer.rights')}
              </p>
              
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
